import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { formatVnd } from "../../shared/i18n/index.ts";
import { PRICE_STEP } from "../../shared/limits.ts";
import { ApiError, api } from "../lib/api.ts";
import { useI18n } from "../lib/i18n.tsx";
import { useMe } from "../lib/me.tsx";

type WatchState = { target_price: number | null; fired_at: number | null } | null;
type Counts = { watching: number; alerts: number };

/**
 * Watch / alert control + sheet (08-design.md §Alert sheet). Target is a
 * 1,000-step strictly below the current price, shown as a % below it.
 */
export function AlertControl({
  listingId,
  currentPrice,
  onCounts,
}: {
  listingId: string;
  currentPrice: number;
  onCounts: (c: Counts) => void;
}) {
  const { t } = useI18n();
  const { me } = useMe();
  const navigate = useNavigate();
  const [state, setState] = useState<WatchState | undefined>(undefined);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<number>(() =>
    Math.max(PRICE_STEP, Math.floor((currentPrice * 0.9) / PRICE_STEP) * PRICE_STEP),
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!me) return;
    let live = true;
    api<WatchState>(`/api/listings/${listingId}/watch`)
      .then((w) => live && setState(w))
      .catch(() => live && setState(null));
    return () => {
      live = false;
    };
  }, [listingId, me]);

  const max = Math.max(PRICE_STEP, currentPrice - PRICE_STEP);
  const pct = currentPrice > 0 ? Math.round(((currentPrice - target) / currentPrice) * 100) : 0;

  async function save(t2: number | null) {
    setError(null);
    try {
      const r = await api<{ kind: string; counts: Counts }>(`/api/listings/${listingId}/watch`, {
        method: "PUT",
        body: { target_price: t2 },
      });
      setState({ target_price: t2, fired_at: null });
      onCounts(r.counts);
      setOpen(false);
    } catch (e) {
      setError(
        e instanceof ApiError && e.body.error === "target_too_high"
          ? t("error.validation")
          : t("error.generic"),
      );
    }
  }

  async function remove() {
    const r = await api<{ counts: Counts }>(`/api/listings/${listingId}/watch`, {
      method: "DELETE",
    }).catch(() => null);
    setState(null);
    if (r) onCounts(r.counts);
    setOpen(false);
  }

  if (!me) {
    return (
      <button
        className="btn btn--block"
        onClick={() => navigate(`/login?next=/l/${listingId}`)}
        data-testid="alert-open"
      >
        {t("alert.set")}
      </button>
    );
  }
  const label = state?.target_price
    ? state.fired_at
      ? t("alert.reached.short")
      : t("alert.target", {
          price: formatVnd(state.target_price),
          pct: `−${Math.round(((currentPrice - state.target_price) / Math.max(1, currentPrice)) * 100)}%`,
        })
    : state
      ? t("alert.watching")
      : t("alert.set");
  return (
    <>
      <button
        className={`btn btn--block${state ? " is-on" : ""}`}
        onClick={() => setOpen((o) => !o)}
        data-testid="alert-open"
      >
        {label}
      </button>
      {open ? (
        <div className="sheet" role="dialog" aria-label={t("alert.set")} data-testid="alert-sheet">
          <label className="field">
            <span className="label">{t("alert.set")}</span>
            <input
              type="range"
              min={PRICE_STEP}
              max={max}
              step={PRICE_STEP}
              value={Math.min(target, max)}
              onChange={(e) => setTarget(Number(e.target.value))}
              data-testid="alert-range"
            />
            <input
              className="input"
              type="number"
              inputMode="numeric"
              min={PRICE_STEP}
              max={max}
              step={PRICE_STEP}
              value={Math.min(target, max)}
              onChange={(e) =>
                setTarget(
                  Math.min(
                    max,
                    Math.max(
                      PRICE_STEP,
                      Math.round(Number(e.target.value) / PRICE_STEP) * PRICE_STEP,
                    ),
                  ),
                )
              }
              data-testid="alert-target"
            />
            <span className="hint">
              {formatVnd(Math.min(target, max))} · −{pct}% · {t("alert.sheet.hint")}
            </span>
          </label>
          {error ? <p className="error">{error}</p> : null}
          <div className="row">
            <button
              className="btn btn--accent"
              onClick={() => void save(Math.min(target, max))}
              data-testid="alert-save"
            >
              {t("common.save")}
            </button>
            <button className="btn" onClick={() => void save(null)} data-testid="watch-only">
              {t("alert.sheet.watch_only")}
            </button>
            {state ? (
              <button
                className="btn btn--ghost"
                onClick={() => void remove()}
                data-testid="watch-remove"
              >
                {t("alert.remove")}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
